import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, Output, EventEmitter, OnInit, OnDestroy, OnChanges } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { ContaDTO } from '../../../models/gerenciar-contas/conta.model';
import { PeriodoFiltro } from '../../../models/dashboard/periodo-filtro.model';
import { ModuloPermissaoDashboard, PERMISSAO_TOTAL } from '../../../models/dashboard/modulo-permissao.model';

interface SelectItem { label: string; value: any; flagUrl?: string; }

@Component({
  selector: 'app-dashboard-header',
  templateUrl: './dashboard-header.component.html',
  styleUrls: ['./dashboard-header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DashboardHeaderComponent implements OnInit, OnDestroy, OnChanges {

  @Input() totalReceita = 0;
  @Input() totalDespesa = 0;
  @Input() totalSaldo = 0;
  @Input() dadosReceitasMensal: number[] = [];
  @Input() dadosDespesasMensal: number[] = [];
  @Input() contas: ContaDTO[] = [];
  @Input() periodoSelecionado!: PeriodoFiltro;
  @Input() contaSelecionada: ContaDTO | null = null;
  @Input() currencySymbol = 'PYG';
  @Input() currencyDigits = '1.0-0';
  @Input() currencyLocale = 'es-PY';
  // Permissoes por modulo (replica moduloDePermissao do mobile)
  @Input() permissoes: ModuloPermissaoDashboard = { ...PERMISSAO_TOTAL };
  // Datas do periodo atual para passar como queryParams na navegacao
  @Input() dataInicio: string | undefined;
  @Input() dataFim: string | undefined;

  // Dados do mes anterior para calculo de tendencia "vs mes anterior"
  @Input() totalReceitaMesAnterior = 0;
  @Input() totalDespesaMesAnterior = 0;
  @Input() totalSaldoMesAnterior = 0;
  @Input() periodoEhMesFechado = true;

  @Output() periodoChanged = new EventEmitter<PeriodoFiltro>();
  @Output() contaChanged = new EventEmitter<ContaDTO | null>();
  @Output() refreshClicado = new EventEmitter<void>();

  texts = {
    saldo_em_conta: '',
    receita: '',
    despesa: '',
    selecione_conta: '',
    pre_definido: '',
    atualizar: '',
    filtro_por_emissao: '',
    vs_mes_anterior: ''
  };

  contaOptions: SelectItem[] = [];

  // Propriedades computadas para os KPI cards
  saldoMensal: number[] = [];
  tendenciaSaldo: { percentual: number; positivo: boolean } | null = null;
  tendenciaReceitas: { percentual: number; positivo: boolean } | null = null;
  tendenciaDespesas: { percentual: number; positivo: boolean } | null = null;

  private langChangeSub!: Subscription;

  constructor(
    private translate: TranslateService,
    private router: Router,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.loadTranslations();
    this.langChangeSub = this.translate.onLangChange.subscribe(() => {
      this.loadTranslations();
      this.buildContaOptions();
      this.cdr.markForCheck();
    });
    this.buildContaOptions();
  }

  ngOnChanges(): void {
    this.buildContaOptions();
    this.recalcularKpis();
  }

  ngOnDestroy(): void {
    if (this.langChangeSub) {
      this.langChangeSub.unsubscribe();
    }
  }

  private loadTranslations(): void {
    this.texts = {
      saldo_em_conta: this.translate.instant('dashboard.texts.saldo_em_conta'),
      receita: this.translate.instant('dashboard.texts.receita'),
      despesa: this.translate.instant('dashboard.texts.despesa'),
      selecione_conta: this.translate.instant('dashboard.texts.selecione_conta'),
      pre_definido: this.translate.instant('dashboard.texts.pre_definido'),
      atualizar: this.translate.instant('dashboard.texts.atualizar'),
      filtro_por_emissao: this.translate.instant('dashboard.texts.filtro_por_emissao'),
      vs_mes_anterior: this.translate.instant('dashboard.texts.vs_mes_anterior')
    };
  }

  private buildContaOptions(): void {
    this.contaOptions = this.contas.map(c => ({
      label: String(c.numeroConta) === '0' ? `${c.tituloDaConta} - EFETIVO` : c.tituloDaConta,
      value: c,
      flagUrl: this.getFlagUrlByMoedaEnum(c.moeda)
    }));
  }

  private recalcularKpis(): void {
    // Saldo mensal = receitas[i] - despesas[i] (usado apenas para sparkline)
    this.saldoMensal = this.dadosReceitasMensal.map(
      (r, i) => r - (this.dadosDespesasMensal[i] || 0)
    );

    // Calcular tendencias usando dados reais do mes anterior (via @Input do pai)
    if (!this.periodoEhMesFechado) {
      this.tendenciaSaldo = null;
      this.tendenciaReceitas = null;
      this.tendenciaDespesas = null;
      return;
    }

    this.tendenciaSaldo = this.calcularTendenciaReal(this.totalSaldo, this.totalSaldoMesAnterior);
    this.tendenciaReceitas = this.calcularTendenciaReal(this.totalReceita, this.totalReceitaMesAnterior);
    this.tendenciaDespesas = this.calcularTendenciaReal(this.totalDespesa, this.totalDespesaMesAnterior);
  }

  /**
   * Gera o atributo "points" de um SVG <polyline> a partir de dados mensais.
   * O SVG viewBox sera "0 0 200 30".
   */
  gerarSparklinePath(dados: number[]): string {
    if (!dados || dados.length === 0) return '';

    const width = 200;
    const height = 30;
    const padding = 2;
    const usableHeight = height - (padding * 2);

    const max = Math.max(...dados, 1);
    const step = width / Math.max(dados.length - 1, 1);

    return dados.map((valor, i) => {
      const x = Math.round(i * step);
      const y = Math.round(padding + usableHeight - (valor / max) * usableHeight);
      return `${x},${y}`;
    }).join(' ');
  }

  /**
   * Gera o path de preenchimento (area abaixo da linha).
   * Fecha o poligono descendo para o fundo do viewBox.
   */
  gerarSparklineAreaPath(dados: number[]): string {
    const linePath = this.gerarSparklinePath(dados);
    if (!linePath) return '';

    const width = 200;
    const height = 30;
    return `0,${height} ${linePath} ${width},${height}`;
  }

  /**
   * Calcula a tendencia (%) comparando mes atual vs mes anterior.
   */
  calcularTendencia(dados: number[]): { percentual: number; positivo: boolean } | null {
    if (!dados || dados.length < 2) return null;

    const mesAtualIndex = new Date().getMonth();
    const mesAnteriorIndex = mesAtualIndex - 1;

    if (mesAnteriorIndex < 0) return null;

    const valorAtual = dados[mesAtualIndex] || 0;
    const valorAnterior = dados[mesAnteriorIndex] || 0;

    if (valorAnterior === 0) {
      return valorAtual > 0 ? { percentual: 100, positivo: true } : null;
    }

    const percentual = ((valorAtual - valorAnterior) / valorAnterior) * 100;
    return {
      percentual: Math.abs(Math.round(percentual * 10) / 10),
      positivo: percentual >= 0
    };
  }

  /**
   * Calcula a tendencia (%) comparando valor atual vs valor do mes anterior (dados reais do backend).
   */
  calcularTendenciaReal(valorAtual: number, valorAnterior: number): { percentual: number; positivo: boolean } | null {
    if (valorAnterior === 0) {
      return valorAtual > 0 ? { percentual: 100, positivo: true } : null;
    }
    const percentual = ((valorAtual - valorAnterior) / valorAnterior) * 100;
    return {
      percentual: Math.abs(Math.round(percentual * 10) / 10),
      positivo: percentual >= 0
    };
  }

  onContaSelect(event: any): void {
    this.contaChanged.emit(event.value);
  }

  onPeriodoChanged(periodo: PeriodoFiltro): void {
    this.periodoChanged.emit(periodo);
  }

  onRefreshClick(): void {
    this.refreshClicado.emit();
  }

  navegarParaTransacoes(): void {
    if (!this.permissoes.conta) return;
    const queryParams = this.buildQueryParams();
    this.router.navigate(['/lista-de-movimentos'], { queryParams });
  }

  navegarParaReceitas(): void {
    if (!this.permissoes.receitas) return;
    const queryParams = this.buildQueryParams();
    this.router.navigate(['/nova-receita'], { queryParams });
  }

  navegarParaDespesas(): void {
    if (!this.permissoes.gastos) return;
    const queryParams = this.buildQueryParams();
    this.router.navigate(['/nova-despesa'], { queryParams });
  }

  // Monta os queryParams com periodo e conta para navegacao (replica logica do mobile)
  private buildQueryParams(): Record<string, string> {
    const params: Record<string, string> = {};
    if (this.dataInicio) {
      params['dataInicial'] = this.dataInicio;
    }
    if (this.dataFim) {
      params['dataFinal'] = this.dataFim;
    }
    if (this.contaSelecionada?.id) {
      params['contaId'] = String(this.contaSelecionada.id);
    }
    return params;
  }

  getFlagUrlByMoedaEnum(moeda: any): string {
    const moedaEnum = (typeof moeda === 'object' && moeda?.enum ? moeda.enum : moeda || '').toString().toUpperCase();
    switch (moedaEnum) {
      case 'GUARANI': return 'assets/images/paraguay-flag.jpg';
      case 'REAL': return 'assets/images/br-flag.svg';
      case 'DOLAR': return 'assets/images/usa-flag.svg';
      case 'PESO': return 'assets/images/argentina-flag.svg';
      default: return '';
    }
  }
}
